import React, { useCallback, useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useDebounce, useStateWithHistory } from "react-use";
import cloneDeep from "lodash/cloneDeep";
import omit from "lodash/omit";
import { UsersContext } from "contexts/UsersContext";

function DefaultMentionSuggestions({
  disabled,
  suggestionsFilter,
  mentionSuggestionsComponent
}) {
  const [userSearch, setUserSearch] = useState("");
  const [mentionSuggestionsOpen, setMentionSuggestionsOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(null);

  const {
    loading: usersLoading,
    error: usersError,
    nonServiceAccountUsers
  } = useContext(UsersContext);

  const [
    debouncedUserSearch,
    setDebouncedUserSearch,
    debouncedUserSearchHistory
  ] = useStateWithHistory("", 5);

  useDebounce(
    () => {
      setDebouncedUserSearch(userSearch);
    },
    300,
    [userSearch]
  );

  useEffect(() => {
    if (!debouncedUserSearch) {
      return;
    }

    const isSearchDifferentFromPrevious =
      debouncedUserSearchHistory.history.length > 2
        ? true
        : debouncedUserSearchHistory.history[
            debouncedUserSearchHistory.position - 1
          ] !==
          debouncedUserSearchHistory.history[
            debouncedUserSearchHistory.position
          ];

    if (!usersError && !usersLoading && isSearchDifferentFromPrevious) {
      setFilteredUsers(
        nonServiceAccountUsers.filter(user =>
          `${user.first_name.toLowerCase()} ${user.last_name.toLowerCase()}`.includes(
            debouncedUserSearch.toLowerCase()
          )
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserSearch]);

  function onUserSearchChange({ value }) {
    setUserSearch(value);
  }

  function transformSuggestions(suggestions) {
    if (suggestions.length === 0) {
      return suggestions;
    }

    return suggestions.map(suggestion => {
      const s = cloneDeep(suggestion);
      s["name"] = `${s.first_name} ${s.last_name}`;
      s["link"] = `${window.location.origin}/users/${s.id}`;
      return omit(s, ["url"]);
    });
  }

  const onMentionSuggestionOpenChanged = useCallback(state => {
    setMentionSuggestionsOpen(state);
  }, []);

  const suggestions = transformSuggestions(
    filteredUsers ?? nonServiceAccountUsers
  );

  if (!mentionSuggestionsComponent) {
    throw new TypeError("mentionSuggestionsComponent argument is required.");
  }
  const MentionSuggestions = mentionSuggestionsComponent;
  return disabled ? null : (
    <MentionSuggestions
      open={mentionSuggestionsOpen}
      onOpenChange={onMentionSuggestionOpenChanged}
      onSearchChange={onUserSearchChange}
      suggestions={suggestionsFilter(userSearch, suggestions)}
    />
  );
}

DefaultMentionSuggestions.propTypes = {
  disabled: PropTypes.bool,
  suggestionsFilter: PropTypes.func.isRequired,
  mentionSuggestionsComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.element
  ])
};

DefaultMentionSuggestions.defaultProps = {
  disabled: false
};

export default DefaultMentionSuggestions;
